import * as React from 'react';
import { action, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import TextField from '@material-ui/core/TextField';
import ArrowForward from '@material-ui/icons/ArrowForward';
import Close from '@material-ui/icons/Close';
import PictureAsPdf from '@material-ui/icons/PictureAsPdf';
import Save from '@material-ui/icons/Save';

import { ActionItem } from '../ActionsMenu/ActionItem';
import { ActionsMenu } from '../ActionsMenu/ActionsMenu';
import {
  CreditRequestModel,
  CreditRequestTransition,
} from '../../models/credit-request';
import { CreditRequestApi } from '../../services/credit-request-api';
import { NotificationUiState } from '../../stores/ui-state/notification-ui-state';

export interface ICreditRequestActionsProps {
  api: CreditRequestApi;
  model: CreditRequestModel;
  notificationUiState: NotificationUiState;
  onSave(): Promise<void> | void;
}

const labels: Record<CreditRequestTransition, string> = {
  [CreditRequestTransition.Submit]: 'Submit',
  [CreditRequestTransition.Resubmit]: 'Resubmit',
  [CreditRequestTransition.Return]: 'Return',
  [CreditRequestTransition.SendToAccountsReceivable]: 'Send to Accounts Receivable',
  [CreditRequestTransition.Reject]: 'Reject',
  [CreditRequestTransition.Close]: 'Close',
  [CreditRequestTransition.Complete]: 'Complete',
};

const successMessages: Record<CreditRequestTransition, string> = {
  [CreditRequestTransition.Submit]: 'Credit request submitted.',
  [CreditRequestTransition.Resubmit]: 'Credit request resubmitted.',
  [CreditRequestTransition.Return]: 'Credit request returned.',
  [CreditRequestTransition.SendToAccountsReceivable]: 'Credit request sent to Accounts Receivable.',
  [CreditRequestTransition.Reject]: 'Credit request rejected.',
  [CreditRequestTransition.Close]: 'Credit request closed.',
  [CreditRequestTransition.Complete]: 'Credit request completed.',
};

const requiresManagerComment = (transition: CreditRequestTransition): boolean =>
  transition === CreditRequestTransition.Return || transition === CreditRequestTransition.Reject;

const supportsManagerComment = (transition: CreditRequestTransition): boolean =>
  requiresManagerComment(transition) ||
  transition === CreditRequestTransition.SendToAccountsReceivable;

const requiresConfirmation = (transition: CreditRequestTransition): boolean =>
  supportsManagerComment(transition) || transition === CreditRequestTransition.Close;

const safeFilenamePart = (value: string, fallback: string): string => {
  const safeValue = value.replace(/[^A-Za-z0-9_-]/g, '');
  return safeValue || fallback;
};

@observer
export class CreditRequestActions extends React.Component<ICreditRequestActionsProps> {
  @observable private selectedTransition: CreditRequestTransition | null = null;
  @observable private comment: string = '';
  @observable private commentError: string = '';
  @observable private busy: boolean = false;

  public render() {
    const { model } = this.props;
    const actionsDisabled = model.isDirty || this.busy;
    return (
      <React.Fragment>
        <ActionsMenu>
          {model.canEdit && (
            <ActionItem
              label="Save"
              disabled={!model.isDirty || this.busy}
              onClick={() => this.save()}
            >
              <Save />
            </ActionItem>
          )}
          {model.availableActions.map(transition => (
            <ActionItem
              key={transition}
              label={labels[transition]}
              disabled={actionsDisabled}
              onClick={() => this.startTransition(transition)}
            >
              <ArrowForward />
            </ActionItem>
          ))}
          <ActionItem
            label="Download PDF"
            disabled={model.id === null || actionsDisabled}
            onClick={() => this.downloadPdf()}
          >
            <PictureAsPdf />
          </ActionItem>
        </ActionsMenu>
        {this.selectedTransition && (
          <div className="full-dialog">
            <div className="dialog-content">
              <h3>{labels[this.selectedTransition]} Credit Request</h3>
              {this.selectedTransition === CreditRequestTransition.Close && (
                <p>Are you sure you want to close this credit request?</p>
              )}
              {supportsManagerComment(this.selectedTransition) && (
                <TextField
                  label="Manager comment"
                  inputProps={{ 'aria-label': 'Manager comment' }}
                  value={this.comment}
                  error={!!this.commentError}
                  helperText={this.commentError || null}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    this.setComment(event.currentTarget.value)
                  }
                  multiline
                  rows={4}
                  fullWidth
                />
              )}
            </div>
            <div className="dialog-actions">
              <div className="close" onClick={() => this.closeDialog()}>
                <span>Cancel</span>
                <Close className="icon" />
              </div>
              <div className="done" onClick={() => this.confirmTransition()}>
                <span>Confirm</span>
                <ArrowForward className="icon" />
              </div>
            </div>
          </div>
        )}
      </React.Fragment>
    );
  }

  @action
  private setComment(comment: string): void {
    this.comment = comment;
    this.commentError = '';
  }

  @action
  private startTransition(transition: CreditRequestTransition): void {
    if (requiresConfirmation(transition)) {
      this.selectedTransition = transition;
      this.comment = '';
      this.commentError = '';
      return;
    }
    void this.executeTransition(transition, '');
  }

  @action
  private closeDialog(): void {
    this.selectedTransition = null;
    this.comment = '';
    this.commentError = '';
  }

  @action
  private confirmTransition(): void {
    if (!this.selectedTransition) return;
    if (requiresManagerComment(this.selectedTransition) && !this.comment.trim()) {
      this.commentError = 'A manager comment is required.';
      return;
    }
    const transition = this.selectedTransition;
    const comment = this.comment.trim();
    this.closeDialog();
    void this.executeTransition(transition, comment);
  }

  private async save(): Promise<void> {
    runInAction(() => this.busy = true);
    try {
      await this.props.onSave();
    } finally {
      runInAction(() => this.busy = false);
    }
  }

  private async executeTransition(transition: CreditRequestTransition, comment: string): Promise<void> {
    const { api, model, notificationUiState } = this.props;
    if (model.id === null || !model.rowVersion) return;
    const requiresReviewValidation =
      transition === CreditRequestTransition.Submit ||
      transition === CreditRequestTransition.Resubmit ||
      transition === CreditRequestTransition.SendToAccountsReceivable;
    if (requiresReviewValidation && !model.validateForReview()) {
      notificationUiState.showError('Please correct the validation errors before submitting.');
      return;
    }
    runInAction(() => this.busy = true);
    try {
      const result = await api.transition(model.id, transition, model.rowVersion, comment);
      model.markSaved(result.request);
      if (result.notificationQueued) {
        notificationUiState.showInfo(successMessages[transition]);
      } else {
        notificationUiState.showInfo(
          'Warning: the credit request action was completed, but the notification could not be sent.',
        );
      }
    } catch {
      notificationUiState.showError(
        'The credit request changed or could not be updated. Reload the page and try again.',
      );
    } finally {
      runInAction(() => this.busy = false);
    }
  }

  private async downloadPdf(): Promise<void> {
    const { api, model, notificationUiState } = this.props;
    if (model.id === null) return;
    runInAction(() => this.busy = true);
    try {
      const blob = await api.downloadPdf(model.id);
      if (!blob) {
        notificationUiState.showError('Unable to download the credit request PDF.');
        return;
      }
      const link = document.createElement('a');
      const href = window.URL.createObjectURL(blob);
      link.download = `CreditRequest_${safeFilenamePart(model.orderNumber, 'order')}_` +
        `${safeFilenamePart(model.customerName, 'customer')}.pdf`;
      link.href = href;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => window.URL.revokeObjectURL(href), 0);
    } catch {
      notificationUiState.showError('Unable to download the credit request PDF.');
    } finally {
      runInAction(() => this.busy = false);
    }
  }
}
